import { Button as AntButton, Modal, Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getStudents } from "../../../../api/campusClasses2.api";
import useAxios from "../../../../hooks/useAxios";
import config from "../../../../lib/config";
import { getCommonCodeLabel } from "../../../../lib/store";
import { IGetStudents, IStudent } from "../../../../models/campusClasses2.model";
import Button from "../../../Button";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import Pagination from "../../../Pagination";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  classCode: string|undefined;
  setStudent: (student: IStudent) => void;
}

const SelectClassStudentModal: FC<IProps> = ({
  visible,
  setVisible,
  classCode,
  setStudent,
}) => {
  const { t, i18n } = useTranslation();
  const { data: res, isLoading, error, execute } = useAxios<IGetStudents>();
  
  const [data, setData] = useState<IStudent[]>([]);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (classCode) {
      execute(getStudents(classCode));
    }
  }, [classCode]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data);
    }
  }, [res, isLoading]);

  const onSearch = () => {
    if (!res) return;

    let _data: IStudent[] = _.cloneDeep(res.data);
    _data = _data.filter((item: any) => item.memberKorName.includes(keyword));

    setData(_data);
  }

  const onClose = () => {
    setKeyword('');
    setVisible(false);
  }

  const columns: TableColumnsType<IStudent> = [
    {
      title: t('class.tutor.column4'),
      dataIndex: 'memberKorName',
      key: 'memberKorName',
      align: 'center',
      sorter: (a, b) => a.memberKorName?.localeCompare(b.memberKorName),
      render: (value, record) => (i18n.language === 'ko') ? value : record.memberEngName
    },
    {
      title: t('class.tutor.column21'),
      dataIndex: 'schoolName',
      key: 'schoolName',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
    },
    {
      title: t('class.tutor.column22'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: value => getCommonCodeLabel('SY0006', value, i18n.language),
    },
    {
      title: t('class.tutor.column23'),
      dataIndex: 'genderDiv',
      key: 'genderDiv',
      align: 'center',
      sorter: (a, b) => a.genderDiv?.localeCompare(b.genderDiv),
      render: value => getCommonCodeLabel('MB0006', value?.trim() || '', i18n.language),
    },
    {
      title: t('class.tutor.column24'),
      dataIndex: 'studySrtDate',
      key: 'studySrtDate',
      align: 'center',
      sorter: (a, b) => a.studySrtDate?.localeCompare(b.studySrtDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('common.column.status'),
      dataIndex: 'classStudentStatusCode',
      key: 'classStudentStatusCode',
      align: 'center',
      sorter: (a, b) => a.classStudentStatusName?.localeCompare(b.classStudentStatusName),
      render: value => getCommonCodeLabel('CL0003', value?.trim() || '', i18n.language),
    },
    {
      title: t('common.button.select'),
      dataIndex: 'classStudentCode',
      key: 'classStudentCode',
      align: 'center',
      render: (value, record) => (
        <div className="row comm-btn__area justify-content-center">
          <Button
            className='btn-basic small mint'
            onClick={() => {
              setStudent(record);
              onClose();
            }}
            text={t('common.button.select')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <Modal
        title={t('class.tutor.title2')}
        visible={visible}
        width={700}
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="cancel" className='btn-basic gray' onClick={onClose}>
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div>
          <div className="flex flex-justify-end">
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={t('class.tutor.placeholder1')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black ml-1"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>            
          </div>
          <div>
            <Table
              className='comm-table__area mt-2'
              columns={columns}
              dataSource={data}
              pagination={{
                current: page,
                pageSize: config.miniPageSize,
              }}
            />
          </div>
          <div className="comm-bx__between">
            <div className="common-paging center">
              <Pagination
                count={data.length}
                page={page}
                setPage={setPage}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SelectClassStudentModal;
